import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

// ---------------------------------------------------------------------------
// OOS map helpers
// ---------------------------------------------------------------------------

/**
 * Parses the variant → OOS boolean map from the hidden JSON block inside
 * the live picker form.
 * @param {HTMLElement} picker
 * @returns {Record<string, boolean>}
 */
function getOosMap(picker) {
  const el = picker.querySelector('[data-variant-oos-map]');
  if (!el) return {};
  try {
    return JSON.parse(el.textContent);
  } catch {
    return {};
  }
}

/**
 * Parses the OOS map from a *parsed response Document* before it has been
 * morphed into the live DOM.
 * @param {Document} responseDoc
 * @returns {Record<string, boolean>}
 */
function getOosMapFromDoc(responseDoc) {
  const el = responseDoc.querySelector('variant-picker [data-variant-oos-map]');
  if (!el) return {};
  try {
    return JSON.parse(el.textContent);
  } catch {
    return {};
  }
}

// ---------------------------------------------------------------------------
// Resolve current variant ID from the live picker
// ---------------------------------------------------------------------------

/**
 * Resolves the active variant ID from the live picker.
 * On multi-option products the clicked radio (e.g. Flavour) has no
 * data-variant-id, so we fall back to the serialised variant JSON block.
 * @param {HTMLElement} picker
 * @returns {string | null}
 */
function resolveActiveVariantId(picker) {
  // 1. A checked input that directly carries a variant id (e.g. Strength buttons)
  const direct = picker.querySelector('fieldset input:checked[data-variant-id]');
  if (direct instanceof HTMLInputElement && direct.dataset.variantId) {
    return direct.dataset.variantId;
  }

  // 2. The serialised selected-variant JSON block (always current after morph)
  const jsonBlock = picker.querySelector(
    'form script[type="application/json"]:not([data-variant-oos-map])'
  );
  if (jsonBlock) {
    try {
      const data = JSON.parse(jsonBlock.textContent);
      if (data?.id) {
        return String(data.id);
      }
    } catch {
      // ignore
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// THE FIX: Pre-patch incoming HTML BEFORE morph runs
// ---------------------------------------------------------------------------

/**
 * Stamps data-metafield-oos / aria-disabled and injects the "Out of stock"
 * label span directly onto the *incoming server HTML Document* before morph
 * touches the live DOM.
 *
 * WHY THIS WORKS:
 * morph.js copies attributes FROM newNode ONTO oldNode (via copyAttributes /
 * updateInput).  If the newNode already has data-metafield-oos="true", morph
 * propagates it to the live DOM automatically.  Previous attempts called
 * applyOosPickerState *after* morph, but morph's onAfterUpdate/updateInput
 * cycle would fire again and strip any attributes that weren't in the new
 * node — creating an infinite fight.  Pre-patching the source wins cleanly.
 *
 * @param {Document} responseDoc  Parsed fetch response document
 * @param {Record<string, boolean>} oosMap  variant id → boolean
 */
function preApplyOosToIncomingHtml(responseDoc, oosMap) {
  if (!oosMap || Object.keys(oosMap).length === 0) return;

  responseDoc.querySelectorAll('variant-picker input[data-variant-id]').forEach((input) => {
    if (!(input instanceof HTMLInputElement)) return;
    const vid = input.dataset.variantId;
    if (!vid) return;

    const isOos = !!oosMap[vid];
    const label = input.closest('.variant-option__button-label');
    if (!label) return;

    if (isOos) {
      input.setAttribute('aria-disabled', 'true');
      input.setAttribute('data-metafield-oos', 'true');
    } else {
      input.removeAttribute('data-metafield-oos');
      if (input.dataset.optionAvailable !== 'false') {
        input.removeAttribute('aria-disabled');
      }
    }
  });
}

// ---------------------------------------------------------------------------
// Buy-button state
// ---------------------------------------------------------------------------

/**
 * Updates Add-to-cart and sticky button disabled state + label text.
 * @param {string | null | undefined} variantId
 * @param {HTMLElement} picker
 */
function applyOosState(variantId, picker) {
  const resolvedId = variantId ?? resolveActiveVariantId(picker);
  if (!resolvedId) return;

  const map = getOosMap(picker);
  const isOos = !!map[resolvedId];

  // Main add-to-cart button(s)
  document.querySelectorAll('add-to-cart-component button[name="add"]').forEach((btn) => {
    if (!(btn instanceof HTMLButtonElement)) return;
    const textEl = btn.querySelector('.add-to-cart-text__content');
    if (!textEl) return;

    // Always refresh defaultText from current text if it's not "Out of stock"
    // This handles cases where the button was morphed and has fresh text from server
    const currentText = textEl.textContent?.trim() ?? '';
    if (!btn.dataset.defaultText || currentText !== 'Out of stock') {
      // Only update defaultText if current text is NOT "Out of stock"
      // This preserves the original "Add to cart" text
      if (currentText !== 'Out of stock') {
        btn.dataset.defaultText = currentText;
      } else if (!btn.dataset.defaultText) {
        // If button says "Out of stock" but we don't have a default, fallback to a common default
        btn.dataset.defaultText = 'Add to cart';
      }
    }

    btn.disabled = isOos;
    textEl.textContent = isOos ? 'Out of stock' : btn.dataset.defaultText;
  });

  // Sticky add-to-cart button
  const stickyBtn = document.querySelector('sticky-add-to-cart button[name="add"]');
  if (stickyBtn instanceof HTMLButtonElement) {
    const span = stickyBtn.querySelector('span');
    if (!span) return;

    if (!stickyBtn.dataset.defaultText) {
      stickyBtn.dataset.defaultText = span.textContent?.trim() ?? '';
    }

    stickyBtn.disabled = isOos;
    span.textContent = isOos ? 'Out of stock' : stickyBtn.dataset.defaultText;
  }
}

// ---------------------------------------------------------------------------
// VariantPicker custom element
// ---------------------------------------------------------------------------

/**
 * @template {import('@theme/component').Refs} [Refs = {}]
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {((event: Event) => void) | undefined} */
  #variantUpdateHandler;

  /**
   * Set when a non-strength option (e.g. Flavour) has just changed, so that
   * after the section morphs we can re-synchronise the Strength option to the
   * first valid variant for the newly selected flavour.
   * @type {boolean}
   */
  #pendingStrengthCorrection = false;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.variantChanged.bind(this));

    // Listen for VariantUpdateEvent on document to re-apply OOS state after button morph
    // This ensures OOS state persists even after product-form-component morphs the button
    // The event bubbles, so we listen on document to catch it
    const handleVariantUpdate = (/** @type {VariantUpdateEvent} */ event) => {
      // Only handle events from this picker's product
      if (event.detail?.data?.productId !== this.dataset.productId && 
          !event.detail?.data?.newProduct) {
        return;
      }
      
      if (event.detail?.resource?.id) {
        const variantId = String(event.detail.resource.id);
        // Use requestYieldCallback to ensure morph completes first
        requestYieldCallback(() => {
          applyOosState(variantId, this);
        });
      }
    };
    
    document.addEventListener('variant:update', handleVariantUpdate);
    // Store handler for cleanup if needed (though custom elements don't always disconnect)
    this.#variantUpdateHandler = handleVariantUpdate;

    // Apply OOS state on first page render
    const initialId = resolveActiveVariantId(this);
    if (initialId) {
      applyOosState(initialId, this);
    }
  }

  /**
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    const optionValueId = selectedOption instanceof HTMLElement ? selectedOption.dataset.optionValueId : undefined;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // When the customer changes a non-strength option (e.g. Flavour), the
    // previously selected Strength may no longer resolve to a valid variant.
    // Flag a post-morph correction so we can auto-select the first valid
    // strength for the new flavour. This must run for the main full picker in
    // BOTH contexts — the product template (PDP) and the homepage
    // `product-information` section — so it is gated on being an interactive
    // main picker rather than on `isOnProductPage` (which is only true on the
    // product template). Skip when the strength itself changed, when inside a
    // product card / quick-add (those use their own resolution), or when
    // navigating to a connected product (that path re-renders <main>).
    const isMainPicker =
      !event.target.closest('product-card') && !event.target.closest('quick-add-dialog');
    const strengthOption = this.#getStrengthOption();
    const changedIsStrength = this.#isStrengthControl(strengthOption, event.target);
    this.#pendingStrengthCorrection = Boolean(
      isMainPicker && strengthOption && !changedIsStrength && !loadsNewProduct
    );

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    // Apply OOS state immediately (optimistic, before fetch resolves).
    // Use resolveActiveVariantId instead of selectedOption.dataset.variantId
    // because Flavour buttons on multi-option products have no variantId.
    const resolvedId = resolveActiveVariantId(this);
    if (resolvedId) {
      applyOosState(resolvedId, this);
    }

    if (isOnProductPage) {
      const variantId = selectedOption.dataset.variantId || null;
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((o) => o.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');
      for (const option of target.options) option.removeAttribute('selected');
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Locates the "Strength" option control within the live picker. The option
   * is identified by its legend/label text (matching the server-side liquid,
   * which special-cases any option whose name contains "strength").
   *
   * @returns {{ kind: 'radio', inputs: HTMLInputElement[] } | { kind: 'select', select: HTMLSelectElement } | null}
   */
  #getStrengthOption() {
    for (const fieldset of this.querySelectorAll('fieldset.variant-option')) {
      const legendText = fieldset.querySelector('legend')?.textContent?.toLowerCase() ?? '';
      if (legendText.includes('strength')) {
        const inputs = /** @type {HTMLInputElement[]} */ (
          Array.from(fieldset.querySelectorAll('input[type="radio"]'))
        );
        return { kind: 'radio', inputs };
      }
    }

    for (const select of this.querySelectorAll('select.variant-option__select')) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const labelText =
        select.closest('.variant-option')?.querySelector('label')?.textContent?.toLowerCase() ?? '';
      const nameText = select.getAttribute('name')?.toLowerCase() ?? '';
      if (labelText.includes('strength') || nameText.includes('strength')) {
        return { kind: 'select', select };
      }
    }

    return null;
  }

  /**
   * Whether the changed control is the Strength option itself.
   *
   * @param {ReturnType<VariantPicker['#getStrengthOption']>} strengthOption
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #isStrengthControl(strengthOption, target) {
    if (!strengthOption) return false;
    if (strengthOption.kind === 'radio') {
      return target instanceof HTMLInputElement && strengthOption.inputs.includes(target);
    }
    return target === strengthOption.select;
  }

  /**
   * Re-synchronises the Strength option after a flavour change. If the checked
   * strength no longer maps to an available variant (it was filtered out, or is
   * out of stock for the new flavour), selects the first strength that does and
   * dispatches a change so the section refreshes to that valid variant.
   */
  #correctStrengthSelection() {
    const strengthOption = this.#getStrengthOption();
    if (!strengthOption) return;

    if (strengthOption.kind === 'radio') {
      const { inputs } = strengthOption;
      /** @param {HTMLInputElement} input */
      const isSelectable = (input) =>
        input.dataset.optionAvailable !== 'false' && input.dataset.metafieldOos !== 'true';

      const checked = inputs.find((input) => input.checked);
      // Already resolves to a valid, available variant — nothing to do.
      if (checked && isSelectable(checked)) return;

      const target = inputs.find(isSelectable);
      // No available strength for this flavour, or it's already selected.
      if (!target || target === checked) return;

      target.checked = true;
      target.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const { select } = strengthOption;
    const options = Array.from(select.options);
    /** @param {HTMLOptionElement} option */
    const isUnavailable = (option) => /unavailable/i.test(option.textContent ?? '');

    const current = select.selectedOptions[0];
    if (current && !isUnavailable(current)) return;

    const target = options.find((option) => !isUnavailable(option)) ?? options[0];
    if (!target || target === current) return;

    select.value = target.value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl =
      selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }

    const sectionId = this.dataset.sectionId;
    const isOnProductPage = this.dataset.templateProductMatch === 'true';
    if (!isOnProductPage && sectionId) {
      params.push(`section_id=${sectionId}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @param {string} requestUrl
   * @param {boolean} shouldMorphMain
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        // Selected variant JSON (first script block, NOT the OOS map)
        const textContent = html.querySelector(
          'variant-picker script[type="application/json"]:not([data-variant-oos-map])'
        )?.textContent;
        
        if (!textContent) return;

        const variantData = JSON.parse(textContent);

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          // ── PRE-PATCH before morph ───────────────────────────────────────
          // Read the OOS map from the response document and stamp OOS
          // attributes onto the incoming HTML nodes BEFORE morph runs.
          // morph will then carry data-metafield-oos into the live DOM itself,
          // so there is no post-morph attribute race.
          const oosMap = getOosMapFromDoc(html);
          preApplyOosToIncomingHtml(html, oosMap);
          // ────────────────────────────────────────────────────────────────

          const newProduct = this.updateVariantPicker(html);

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(variantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }

          // Apply OOS state AFTER VariantUpdateEvent is dispatched
          // This ensures it runs after product-form-component morphs the button
          // Use requestYieldCallback to ensure morph completes first
          requestYieldCallback(() => {
            if (variantData?.id) {
              applyOosState(String(variantData.id), this);
            }

            // After a flavour (non-strength) change, ensure the strength
            // selection still resolves to a valid variant; otherwise pick the
            // first valid one for the new flavour.
            if (this.#pendingStrengthCorrection) {
              this.#pendingStrengthCorrection = false;
              this.#correctStrengthSelection();
            }
          });
        }
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {{ id: string, url: string }} NewProduct
   */

  /**
   * @param {Document} newHtml
   * @returns {NewProduct | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) throw new Error('No new variant picker source found');

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // morph now carries the pre-patched OOS attributes — no post-morph fixup needed
    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * @param {Document} newHtml
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
  }

  /** @returns {HTMLInputElement | HTMLOptionElement | undefined} */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }
    return selectedOption;
  }

  /** @returns {string | undefined} */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error('No option value ID found');
    return optionValueId;
  }

  /** @returns {string[]} */
  get selectedOptionsValues() {
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked')
    );
    return selectedOptions.map((option) => {
      if (!(option instanceof HTMLElement)) throw new Error('Option is not an HTMLElement');
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}